import { useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { SPIScorecard } from "./SPIScorecard";
import { SPILoadingState } from "./SPILoadingState";
import { useListScorecards, useCreateScorecard, useGetScorecard, SCORECARD_ERROR_MESSAGE } from "@/resources/scorecard";
import type { SPIResult, CosmoQuestion } from "@/types/scorecard";
import { Loader2, ClipboardCheck, Plus, RefreshCw, Download, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useViewerReadOnly } from "@/contexts/ViewerReadOnlyContext";
import { useScorecardGeneratingState } from "@/hooks/useScorecardGeneratingState";
import { ProductSectionEmptyState } from "@/components/product-detail/ProductSectionEmptyState";
import { TabHeader } from "@/components/shared/TabHeader";
import { productApi } from "@/resources/product";
import { useAppData } from "@/contexts/AppDataContext";
import { RoutePath } from "@/routes";
import type { CreateABTestLocationState } from "@/pages/CreateABTest";

interface SPIScorecardTabProps {
  asin: string;
  brandId?: number;
  productId?: number;
}

export function SPIScorecardTab({ asin, brandId, productId }: SPIScorecardTabProps) {
  const readOnly = useViewerReadOnly();
  const navigate = useNavigate();
  const { selectedBrandId } = useAppData();
  const [selectedId, setSelectedId] = useState<number | undefined>(undefined);
  const { generatingId, startedAt, setGenerating, clearGenerating } = useScorecardGeneratingState(asin);
  const [pendingId, setPendingId] = useState<number | undefined>(generatingId);
  const [completedResult, setCompletedResult] = useState<SPIResult | null>(null);
  const pendingIdRef = useRef<number | undefined>(generatingId);
  const qc = useQueryClient();

  const { data: list, isLoading: listLoading } = useListScorecards(asin, brandId);
  const latestId = selectedId ?? list?.[0]?.id;
  const { data: scorecardData } = useGetScorecard(pendingId ? undefined : latestId);

  // The row action's payload needs the full hero-product record, the same one the SEO
  // Strategy tab hands over when it starts a test from a gap (`SeoStrategyTab.tsx`).
  const { data: heroAsinData } = productApi.useGetHeroProductABTests(
    productId !== undefined ? String(productId) : undefined,
    selectedBrandId ?? undefined,
  );

  const createScorecard = useCreateScorecard();

  const resetTab = useCallback(() => {
    setPendingId(undefined);
    pendingIdRef.current = undefined;
    setCompletedResult(null);
    setSelectedId(undefined);
    clearGenerating();
  }, [clearGenerating]);

  const handleGenerate = useCallback(() => {
    if (createScorecard.isPending) return;
    createScorecard.mutate(
      { asin, brandId },
      {
        onSuccess: (data) => {
          pendingIdRef.current = data.id;
          setPendingId(data.id);
          setGenerating(data.id);
        },
        onError: () => {
          toast.error(SCORECARD_ERROR_MESSAGE);
          resetTab();
        },
      }
    );
  }, [asin, brandId, createScorecard, resetTab, setGenerating]);

  const handleLoadingComplete = useCallback((result: SPIResult) => {
    const completedId = pendingIdRef.current;
    setCompletedResult(result);
    if (completedId !== undefined) {
      setSelectedId(completedId);
    }
    setPendingId(undefined);
    pendingIdRef.current = undefined;
    clearGenerating();
    qc.invalidateQueries({ queryKey: ["scorecards", "list", asin] });
  }, [asin, qc, clearGenerating]);

  const handleLoadingError = useCallback(() => {
    toast.error(SCORECARD_ERROR_MESSAGE);
    resetTab();
  }, [resetTab]);

  const handleSelectScorecard = useCallback((id: number) => {
    setSelectedId(id);
    setCompletedResult(null);
  }, []);

  const handleCreateAbTest = useCallback((question: CosmoQuestion) => {
    if (!heroAsinData || productId === undefined) return;
    const state: CreateABTestLocationState = {
      heroAsin: heroAsinData,
      productId,
      initialTargetPhrase: question.relationKey,
      prefillFromScorecard: {
        question: question.question,
        recommendation: question.recommendation,
        evidence: question.evidenceSnippet,
        category: question.category,
        score: question.score,
        status: question.status,
        finding: question.finding,
      },
      defaultSource: "scorecard",
      returnTo: window.location.pathname + window.location.search,
    };
    navigate(RoutePath.CreateABTest, { state });
  }, [heroAsinData, productId, navigate]);

  const initialElapsed = startedAt ? Date.now() - startedAt : undefined;

  // AUT-437: every state of the tab carries the shared header. While a score is loading or
  // generating the actions are present but disabled, with the kebab items disabled too.
  const pendingHeader = (
    <TabHeader
      title="Amazon AI Score"
      menuItems={
        <>
          <DropdownMenuItem disabled>
            <Download className="h-4 w-4 mr-2" /> Export
          </DropdownMenuItem>
          {!readOnly ? (
            <DropdownMenuItem disabled>
              <Share2 className="h-4 w-4 mr-2" /> Share
            </DropdownMenuItem>
          ) : null}
        </>
      }
    >
      {!readOnly ? (
        <Button disabled className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      ) : null}
    </TabHeader>
  );

  if (pendingId !== undefined) {
    return (
      <div className="min-w-0 space-y-6">
        {pendingHeader}
        <SPILoadingState
          scorecardId={pendingId}
          onComplete={handleLoadingComplete}
          onError={handleLoadingError}
          initialElapsed={initialElapsed}
        />
      </div>
    );
  }

  if (listLoading) {
    return (
      <div className="min-w-0 space-y-6">
        {pendingHeader}
        <div className="flex flex-1 items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  const result: SPIResult | null =
    completedResult ??
    (scorecardData && scorecardData.status === "completed" ? (scorecardData as SPIResult & typeof scorecardData) : null);

  if (!result && (!list || list.length === 0)) {
    return (
      <div className="min-w-0 space-y-6">
        <TabHeader title="Amazon AI Score">
          {!readOnly ? (
            <Button onClick={handleGenerate} disabled={createScorecard.isPending} className="gap-2">
              <RefreshCw className={`h-4 w-4 ${createScorecard.isPending ? "animate-spin" : ""}`} />
              {createScorecard.isPending ? "Refreshing..." : "Refresh"}
            </Button>
          ) : null}
        </TabHeader>
        <ProductSectionEmptyState
          icon={ClipboardCheck}
          title="No Amazon AI Score yet"
          description="Generate an Amazon AI Score to get an AI-powered analysis of your product listing — title, bullets, images, and keyword coverage scored against best practices."
          actions={readOnly ? [] : [
            {
              label: createScorecard.isPending ? "Generating…" : "Generate Amazon AI Score",
              icon: createScorecard.isPending ? Loader2 : Plus,
              onClick: handleGenerate,
            },
          ]}
        />
      </div>
    );
  }

  if (!result) {
    return (
      <div className="min-w-0 space-y-6">
        {pendingHeader}
        <div className="flex items-center justify-center min-h-[40vh]" />
      </div>
    );
  }

  return (
    <SPIScorecard
      result={result}
      onBack={() => {}}
      hideBackButton
      hideProductInfo
      asin={asin}
      brandId={brandId}
      onSelectScorecard={handleSelectScorecard}
      selectedScorecardId={latestId}
      scorecardList={list}
      readOnly={readOnly}
      onRerun={readOnly ? undefined : handleGenerate}
      rerunning={createScorecard.isPending}
      onCreateAbTest={!readOnly && productId !== undefined && heroAsinData ? handleCreateAbTest : undefined}
      variant="product-tab"
    />
  );
}
